import React, { useEffect, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import FamilyMemberDialog from "./FamilyMemberDialog";
import FamilyMembersList from "./FamilyMembersList";
import "./tree.css";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MDTypography from "components/MDTypography";
import { useSelector, useDispatch } from "react-redux";
import FamilyTreeUi from "./components/FamilyTreeUi";
import { fetchFamilyTreeGraph, fetchFamilyMembers, fetchUserProfile, deleteFamilyMember } from "../../services/familyTreeService";
import { setUser } from "../../Redux/action/sessionAction";

const normalizeId = (value) => {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  if (!str || str.toLowerCase() === "null" || str.toLowerCase() === "undefined") return null;
  return str;
};

const normalizeGender = (value) => {
  const gender = (value || "").toString().trim().toLowerCase();
  if (gender === "male" || gender === "female") return gender;
  if (gender === "m") return "male";
  if (gender === "f") return "female";
  return "male";
};

const normalizeMembers = (members = []) => {
  const normalized = (members || []).map((raw) => {
    const normalizedId = normalizeId(raw.id) || normalizeId(raw.record_id);
    const parentId = normalizeId(
      raw.parentId ??
      raw.parent_id ??
      raw.parentid_c ??
      raw.parent ??
      raw.parentID ??
      raw.parent_record_id
    );
    const spouseId = normalizeId(
      raw.spouseId ??
      raw.spouse_id ??
      raw.spouseid_c ??
      raw.spouse ??
      raw.spouseID
    );
    const gender = normalizeGender(raw.gender ?? raw.gender_c ?? raw.genderLabel ?? raw.gender_label);

    return {
      ...raw,
      id: normalizedId || (raw.id !== undefined ? String(raw.id) : null),
      parentId,
      spouseId,
      gender,
    };
  });

  const byId = new Map();
  normalized.forEach((person) => {
    if (person.id) {
      person.id = String(person.id);
      byId.set(person.id, person);
    }
  });

  normalized.forEach((person) => {
    if (person.parentId) {
      person.parentId = String(person.parentId);
      if (!byId.has(person.parentId)) {
        person.parentId = null;
      }
    }
  });

  normalized.forEach((person) => {
    if (person.spouseId) {
      person.spouseId = String(person.spouseId);
      const spouse = byId.get(person.spouseId);
      if (!spouse) {
        person.spouseId = null;
        return;
      }
      if (!spouse.spouseId) {
        spouse.spouseId = person.id;
      }
    }
  });

  return normalized;
};

const FamilyTree = () => {
  const dispatch = useDispatch();
  const sessionId = useSelector((state) => state.session.sessionId || null);
  const userId = useSelector((state) =>
    state.session.setUserId ? state.session.setUserId.value : null
  );
  const user = useSelector((state) =>
    state.session.setUser ? state.session.setUser : null
  );
  const userPhoto = useSelector((state) =>
    state.session.setUserPhoto ? state.session.setUserPhoto : null
  );

  const [members, setMembers] = useState([]);
  // Do not default to a stored Family Group ID; only use one if explicitly set in state or localStorage
  const [familyGroupId, setFamilyGroupId] = useState("");
  const [membersLoading, setMembersLoading] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyMembersLoading, setFamilyMembersLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false); // Modal state
  const [userProfile, setUserProfile] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [userProfileTrigger, setUserProfileTrigger] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [error, setError] = useState(null);

  const refreshFamilyData = () => setRefreshTrigger((prev) => prev + 1);
  const refreshUserProfile = () => setUserProfileTrigger((prev) => prev + 1);

  const ensureMyselfFirst = (list = []) => {
    if (!Array.isArray(list) || list.length === 0) return [];
    const ordered = [...list];
    const currentUserId = normalizeId(userId);

    const selfIndex = ordered.findIndex((member) => {
      if (!member) return false;
      const linkedId = normalizeId(member.linkedUserId || member.linked_user_id_c || member.linked_user_id || member.linked_userid);
      if (linkedId && currentUserId) return linkedId === currentUserId;
      const relationship = (member.relationship_type_c || member.relationship || "")
        .toString()
        .trim()
        .toLowerCase();
      if (relationship !== "myself") return false;
      if (!currentUserId) return true;
      const ownerId = normalizeId(member.userId || member.users_id_c || member.owner_id);
      return ownerId ? ownerId === currentUserId : true;
    });

    if (selfIndex > 0) {
      const [selfMember] = ordered.splice(selfIndex, 1);
      ordered.unshift(selfMember);
    }

    return ordered;
  };

  const fetchFamilyTree = async () => {
    // Prefer explicit in-state familyGroupId; if not set, look in localStorage.
    const effectiveFamilyGroupId = familyGroupId || localStorage.getItem("familyGroupId") || "";
    if (!effectiveFamilyGroupId) {
      console.warn("No familyGroupId provided or found in localStorage");
      setFamilyGroupId("");
      return;
    }
    // Update the state so the UI shows the effective familyGroupId
    if (!familyGroupId) setFamilyGroupId(effectiveFamilyGroupId);

    try {
      setMembersLoading(true);
      const data = await fetchFamilyTreeGraph(effectiveFamilyGroupId);

      if (data?.status === "success" && Array.isArray(data.members)) {
        const cleaned = normalizeMembers(data.members);
        setMembers(cleaned);
      } else {
        console.error("Invalid response:", data);
      }
    } catch (err) {
      console.error("Error fetching family tree:", err);
    } finally {
      setMembersLoading(false);
    }
  };

  useEffect(() => {
    fetchFamilyTree();
  }, [refreshTrigger, familyGroupId]);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!sessionId || !userId) return;

      try {
        const profileData = await fetchUserProfile(sessionId, userId);
        if (profileData) {
          setUserProfile(profileData);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    loadUserProfile();
  }, [sessionId, userId, userProfileTrigger]);

  useEffect(() => {
    const fetchFamilyMemberData = async () => {
      if (!sessionId || !userId || !userProfile) return;

      setFamilyMembersLoading(true);
      setError(null);

      try {
        const membersData = await fetchFamilyMembers(sessionId, userId);
        const orderedMembers = ensureMyselfFirst(Array.isArray(membersData) ? membersData : []);
        setFamilyMembers(orderedMembers);
      } catch (fetchError) {
        console.error("Error fetching family members:", fetchError);
        setError("Failed to load family members. Please try again.");
      } finally {
        setFamilyMembersLoading(false);
      }
    };

    fetchFamilyMemberData();
  }, [sessionId, userId, userProfile, refreshTrigger, userPhoto]);

  const generateID = () => {
    return familyMembers.length.toString();
  };

  const handleAddMember = async (newEntry) => {
    setFamilyMembers((prev) => ensureMyselfFirst([...prev, newEntry]));
    setOpenDialog(false);
    refreshFamilyData();
    // Immediately refetch members from backend so the tree updates
    try {
      await fetchFamilyTree();
    } catch (_) {
      // non-blocking: fall back to local state only
    }
  };

  const handleUpdateMember = (updatedMember) => {
    setFamilyMembers((prevMembers) =>
      ensureMyselfFirst(
        prevMembers.map((member) => (member.id === updatedMember.id ? updatedMember : member))
      )
    );

    if (updatedMember.id === "0" && user) {
      const nameParts = updatedMember.name.split(" ");
      const updatedUser = {
        ...user,
        first_name: { value: nameParts[0] || updatedMember.name },
        last_name: { value: nameParts.slice(1).join(" ") || "" },
        phone_mobile: { value: updatedMember.mobile },
        aadhar: { value: updatedMember.aadhar },
        address_street: { value: updatedMember.address },
        address_city: { value: updatedMember.city },
        address_state: { value: updatedMember.state },
        address_country: { value: updatedMember.country },
        caste_c: { value: updatedMember.caste },
        religion_c: { value: updatedMember.religion },
        photo: { value: updatedMember.photo || "" },
      };
      dispatch(setUser(updatedUser));
    }
  };

  const handleDeleteClick = (memberId) => {
    if (!memberId) return;
    if (memberId === "0") {
      alert("You cannot delete 'Myself'!");
      return;
    }
    setMemberToDelete(memberId);
    setDeleteConfirm(true);
  };

  const closeDeleteDialog = () => {
    setDeleteConfirm(false);
    setMemberToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete) return;
    setFamilyMembersLoading(true);
    setError(null);

    try {
      await deleteFamilyMember(sessionId, memberToDelete);
      const updatedMembers = familyMembers.filter((member) => member.id !== memberToDelete);
      setFamilyMembers(ensureMyselfFirst(updatedMembers));
      setDeleteConfirm(false);
      setMemberToDelete(null);
      refreshFamilyData();
    } catch (deleteError) {
      console.error("Error deleting family member:", deleteError);
      setError("Failed to delete family member. Please try again.");
    } finally {
      setFamilyMembersLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="min-h-screen pt-0 sm:pt-8 px-0 sm:px-8 pb-0 sm:pb-8 bg-gradient-to-r from-gray-200 to-gray-100">
        <div className="w-full mx-auto">
          {/* <FamilyTreeDemo members={members}/> */}

          <FamilyTreeUi
            members={members}
            loading={membersLoading}
            onOpenAddFamily={() => setOpenDialog(true)}
            onLinkSuccess={fetchFamilyTree}
            familyGroupId={familyGroupId}
          />
          <FamilyMembersList
            familyMembers={familyMembers}
            loading={familyMembersLoading}
            onDelete={handleDeleteClick}
            onUpdate={handleUpdateMember}
            refreshFamilyData={refreshFamilyData}
            refreshUserProfile={refreshUserProfile}
            onOpenAddFamily={() => setOpenDialog(true)}
            familyGroupId={familyGroupId}
          />

          {/* Add Family Member Modal */}
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            fullWidth
            maxWidth={false}
            fullScreen={window.innerWidth < 768}
            PaperProps={{
              sx: {
                width: { xs: '100vw', sm: '90vw', md: '80vw' },
                maxWidth: { xs: '100vw', sm: '90vw', md: '80vw' },
                maxHeight: { xs: '100vh', sm: '90vh' },
                margin: { xs: 0, sm: 2 },
              },
            }}
          >
            <DialogTitle className="text-xl sm:text-2xl font-semibold text-center text-gray-700 mb-4 sm:mb-6 px-3 sm:px-4">
              Add Family Member
              <IconButton
                onClick={() => setOpenDialog(false)}
                sx={{ position: "absolute", right: { xs: 4, sm: 8 }, top: { xs: 4, sm: 8 } }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <div className="px-3 sm:px-4 md:px-6 py-2 sm:py-4 overflow-y-auto">
              <FamilyMemberDialog
                mode="add"
                onAddMember={handleAddMember}
                generateID={generateID}
                familyMembers={familyMembers}
                onClose={() => setOpenDialog(false)}
                familyGroupId={familyGroupId}
              />
            </div>
          </Dialog>

          <Dialog open={deleteConfirm} onClose={closeDeleteDialog}>
            <DialogTitle fontWeight="bold">Confirm Delete</DialogTitle>
            <DialogContent>
              <MDTypography>
                Are you sure you want to delete this family member?
              </MDTypography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={closeDeleteDialog}
                sx={{
                  color: "error.main",
                  "&:hover": {
                    color: "error.main",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleDeleteConfirm} sx={{ color: "success.main" }} autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FamilyTree;
